import { SCHEDULED_CHAT_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	fetchScheduledChatStudioInfo,
	fetchStudioSc3RuleResponse,
	type FetchScheduledChatStudioInfoParams,
} from '@atlassian/conversation-assistant-message-actions-api/scheduled-chat';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	isStoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

const getRuleId = (action: MessageAction): string | undefined => {
	if (action.key !== SCHEDULED_CHAT_KEY || action.status !== 'finished') return undefined;
	const data = action.data as { ruleIdUuid?: unknown } | null;
	const raw = data?.ruleIdUuid;
	return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
};

const getExecutionData = (action: MessageAction): Record<string, unknown> => {
	const data = action.executedData;
	return data && typeof data === 'object' && !Array.isArray(data)
		? (data as Record<string, unknown>)
		: {};
};

const collectDeletedRuleIds = (messages: StoreChatItem[], ids: Set<string>): void => {
	for (const item of messages) {
		if (!isStoreAssistant(item)) continue;
		for (const action of item.content.message.message.actions ?? []) {
			const ruleId = getRuleId(action);
			if (ruleId && getExecutionData(action).deletedRuleId === ruleId) ids.add(ruleId);
		}
	}
};

/** Keep known deletions and resolve unknown ones before history is published to the UI. */
export async function hydrateScheduledChatRuleStatuses({
	messages,
	getCachedMessages,
	studioInfoParams,
}: {
	messages: StoreChatItem[];
	getCachedMessages: () => StoreChatItem[];
	studioInfoParams?: FetchScheduledChatStudioInfoParams;
}): Promise<StoreChatItem[]> {
	const deletedRuleIds = new Set<string>();
	collectDeletedRuleIds(messages, deletedRuleIds);
	collectDeletedRuleIds(getCachedMessages(), deletedRuleIds);
	const uncheckedRuleIds = new Set<string>();
	for (const item of messages) {
		if (!isStoreAssistant(item)) continue;
		for (const action of item.content.message.message.actions ?? []) {
			const ruleId = getRuleId(action);
			if (ruleId && !deletedRuleIds.has(ruleId)) uncheckedRuleIds.add(ruleId);
		}
	}
	if (studioInfoParams && uncheckedRuleIds.size > 0) {
		try {
			const info = await fetchScheduledChatStudioInfo(studioInfoParams);
			if (info) {
				await Promise.allSettled(
					[...uncheckedRuleIds].map(async (ruleId) => {
						const response = await fetchStudioSc3RuleResponse({ ...info, ruleId });
						if (response?.status === 404) deletedRuleIds.add(ruleId);
					}),
				);
			}
		} catch {
			// A failed lookup must not prevent chat loading or imply deletion.
		}
	}
	// A delete can finish while history or the rule checks are in flight.
	collectDeletedRuleIds(getCachedMessages(), deletedRuleIds);
	if (deletedRuleIds.size === 0) return messages;
	return messages.map((item) => {
		if (!isStoreAssistant(item)) return item;
		const message = item.content.message.message;
		let changed = false;
		const actions = message.actions?.map((action) => {
			const ruleId = getRuleId(action);
			const executedData = getExecutionData(action);
			if (!ruleId || !deletedRuleIds.has(ruleId) || executedData.deletedRuleId === ruleId)
				return action;
			changed = true;
			return { ...action, executedData: { ...executedData, deletedRuleId: ruleId } };
		});
		return changed
			? {
					...item,
					content: {
						...item.content,
						message: { ...item.content.message, message: { ...message, actions } },
					},
				}
			: item;
	});
}
